/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  DropdownOption,
  DropdownOption$inboundSchema,
  DropdownOption$Outbound,
  DropdownOption$outboundSchema,
} from "./dropdownoption.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The variant of the button to appear on the dropdown.
 */
export const DropdownComponentConfigButtonVariant = {
  Default: "default",
  Tier2: "tier2",
  Tier3: "tier3",
  Brand: "brand",
  Destructive: "destructive",
  Inverse: "inverse",
  Link: "link",
  Transparent: "transparent",
} as const;
/**
 * The variant of the button to appear on the dropdown.
 */
export type DropdownComponentConfigButtonVariant = OpenEnum<
  typeof DropdownComponentConfigButtonVariant
>;

/**
 * The configuration for a dropdown.
 */
export type DropdownComponentConfig = {
  /**
   * The variant of the button to appear on the dropdown.
   */
  buttonVariant?: DropdownComponentConfigButtonVariant | undefined;
  /**
   * Whether the dropdown is disabled.
   */
  disabled?: boolean | undefined;
  /**
   * The id of the dropdown. This is used to uniquely identify the dropdown component in the UI.
   */
  id?: string | undefined;
  /**
   * The options of the dropdown.
   */
  options: Array<DropdownOption>;
  /**
   * Whether to show the action icon on the currently selected option in the dropdown. If false, the action icon will not be shown.
   */
  showIcon?: boolean | undefined;
  /**
   * The subtitle of the dropdown. This is displayed above the dropdown options.
   */
  subtitle?: string | undefined;
  /**
   * A reference to a reading or setting.
   */
  valueRef: Ref;
};

/** @internal */
export const DropdownComponentConfigButtonVariant$inboundSchema: z.ZodType<
  DropdownComponentConfigButtonVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(DropdownComponentConfigButtonVariant);
/** @internal */
export const DropdownComponentConfigButtonVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  DropdownComponentConfigButtonVariant
> = openEnums.outboundSchema(DropdownComponentConfigButtonVariant);

/** @internal */
export const DropdownComponentConfig$inboundSchema: z.ZodType<
  DropdownComponentConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  button_variant: DropdownComponentConfigButtonVariant$inboundSchema.default(
    "tier2",
  ),
  disabled: types.boolean().default(false),
  id: types.optional(types.string()),
  options: z.array(DropdownOption$inboundSchema),
  show_icon: types.boolean().default(false),
  subtitle: types.optional(types.string()),
  value_ref: Ref$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "button_variant": "buttonVariant",
    "show_icon": "showIcon",
    "value_ref": "valueRef",
  });
});
/** @internal */
export type DropdownComponentConfig$Outbound = {
  button_variant: string;
  disabled: boolean;
  id?: string | undefined;
  options: Array<DropdownOption$Outbound>;
  show_icon: boolean;
  subtitle?: string | undefined;
  value_ref: Ref$Outbound;
};

/** @internal */
export const DropdownComponentConfig$outboundSchema: z.ZodType<
  DropdownComponentConfig$Outbound,
  z.ZodTypeDef,
  DropdownComponentConfig
> = z.object({
  buttonVariant: DropdownComponentConfigButtonVariant$outboundSchema.default(
    "tier2",
  ),
  disabled: z.boolean().default(false),
  id: z.string().optional(),
  options: z.array(DropdownOption$outboundSchema),
  showIcon: z.boolean().default(false),
  subtitle: z.string().optional(),
  valueRef: Ref$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    buttonVariant: "button_variant",
    showIcon: "show_icon",
    valueRef: "value_ref",
  });
});

export function dropdownComponentConfigToJSON(
  dropdownComponentConfig: DropdownComponentConfig,
): string {
  return JSON.stringify(
    DropdownComponentConfig$outboundSchema.parse(dropdownComponentConfig),
  );
}
export function dropdownComponentConfigFromJSON(
  jsonString: string,
): SafeParseResult<DropdownComponentConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DropdownComponentConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DropdownComponentConfig' from JSON`,
  );
}
